import { useCallback, useState } from 'react'
import axios from 'axios'
import * as Sentry from '@sentry/react'

type DefaultState = {
  ok: false,
  loading: false,
  error: false
}

type SuccessState = {
  ok: true,
  loading: false,
  error: false
}

type LoadingState = {
  ok: false,
  loading: true,
  error: false
}

type ErrorState = {
  ok: false,
  loading: false,
  error: true
}

export type UseS3UploadState = DefaultState | SuccessState | LoadingState | ErrorState

export type UploadFilesToS3Param = {
  url: string,
  file: File
}[]

type UploadFilesToS3Error = {
  error: true,
  ok: false
}

type UploadFilesToS3Success = {
  error: false,
  ok: true
}

export type UploadFilesToS3Result = UploadFilesToS3Error | UploadFilesToS3Success

export type UseS3UploadResult = [
  (itemsToUpload: UploadFilesToS3Param) => Promise<UploadFilesToS3Result>,
  UseS3UploadState
]

const defaultState: DefaultState = {
  error: false,
  loading: false,
  ok: false
}

const loadingState: LoadingState = {
  error: false,
  loading: true,
  ok: false
}

const successState: SuccessState = {
  error: false,
  loading: false,
  ok: true
}

const errorState: ErrorState = {
  error: true,
  loading: false,
  ok: false
}

export const useS3Upload = (): UseS3UploadResult => {
  const [state, setState] = useState<UseS3UploadState>(defaultState)

  const uploadFilesToS3 = useCallback(async (itemsToUpload: UploadFilesToS3Param): Promise<UploadFilesToS3Result> => {
    try {
      setState(loadingState)
      const filePromises = itemsToUpload.map(item => axios.put(item.url, item.file, { headers: { 'Content-Type': item.file.type } }))
      await Promise.all(filePromises)
      setState(successState)
      return {
        error: false,
        ok: true
      }
    } catch (err) {
      Sentry.captureException(err)
      setState(errorState)
      return {
        error: true,
        ok: false
      }
    }
  }, [])

  return [uploadFilesToS3, state]
}
